/**
 * useFormattedRichText
 *
 * Resolves a richtext token like `<RichTextContent>` does, but lets the caller
 * reshape property values before embedded field tokens are substituted (e.g.
 * collapsing a "4-4" bedroom range to "4"). Cross-link pruning and paragraph
 * cleanup mirror the runtime's RichTextContent. The caller renders the result
 * inside its own `<EditableToken type="richtext">`.
 */

import { useMemo } from 'react';
import { useVibesites, useContentToken } from '@vibesites/runtime/react';
import { resolveRichText, stripEdgeEmptyParagraphs, verifyCrossLinks } from '@vibesites/runtime';

type PropertyRecord = Record<string, any>;
type RichTextTag = 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'li' | 'figcaption';

const PHRASING_ONLY_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'a', 'label']);
const BLANK_P_RE = /<p\b[^>]*>(?:\s|<br\s*\/?>)*<\/p>/gi;
const BLOCK_CONTENT_RE = /<(?:p|div|h[1-6]|ul|ol|li|blockquote)[\s>]/i;

function stripWrappingParagraph(html: string, tag: string): string {
  if (!PHRASING_ONLY_TAGS.has(tag)) return html;
  const cleaned = stripEdgeEmptyParagraphs(html);
  const match = cleaned.match(/^<p>([\s\S]*)<\/p>$/);
  if (!match) return cleaned || html;
  const inner = match[1];
  if (/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(inner)) return cleaned;
  return inner;
}

export function useFormattedRichText(
  path: string,
  formatProperty: (property: PropertyRecord) => PropertyRecord,
  tag: RichTextTag = 'div',
): { html: string; tag: RichTextTag } {
  const { property, pagePaths } = useVibesites();
  const rawHtml = useContentToken(path);
  const formattedProperty = useMemo(
    () => formatProperty(property as PropertyRecord),
    [formatProperty, property],
  );

  const resolved = rawHtml ? resolveRichText(rawHtml, formattedProperty) : '';
  const html = stripWrappingParagraph(verifyCrossLinks(resolved, pagePaths), tag).replace(BLANK_P_RE, '');
  // Phrasing-only tags can't hold block children; promote to div to keep SSR hydration valid.
  const effectiveTag = PHRASING_ONLY_TAGS.has(tag) && BLOCK_CONTENT_RE.test(html) ? 'div' : tag;
  return { html, tag: effectiveTag };
}
